import moment from "moment";

import type { Dispatch, GetState } from "common/types/redux";

import { pushEnrichedEventToDataLayerWithCleanUp } from "seneca-common/features/analytics";
import {
  getAssignmentProgress,
  makeGetAssignmentsForQuiz,
  makeGetAssignmentsForSection
} from "seneca-common/features/assignments/features/student-assignments/state/selectors";
import {
  getSessionCourseId,
  getSessionScore,
  getSessionSectionId
} from "seneca-common/features/session/selectors/session";
import {
  analyticsCategories,
  logGTMTrackingEvent,
  withLogAndSwallowError
} from "seneca-common/utils/senecaAnalytics";

import makeGetAssignmentProgress from "features/classroom/features/sidebar/features/assignments/selectors/makeGetAssignmentProgress";

import generateSessionAnalyticsPayload from "./payload";

const namespace = "seneca/session";

export const analyticsLogClickStartAdaptiveSession = withLogAndSwallowError(
  logClickStartAdaptiveSession
);

function logClickStartAdaptiveSession(courseId: string, sectionId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/CLICK_START_ADAPTIVE_SESSION`,
        payload: generateSessionAnalyticsPayload(state, courseId, sectionId)
      },
      state
    );
  };
}

export const analyticsLogClickStartWrongAnswersSession = withLogAndSwallowError(
  logClickStartWrongAnswersSession
);

function logClickStartWrongAnswersSession(courseId: string, sectionId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/CLICK_START_WRONG_ANSWERS_SESSION`,
        payload: generateSessionAnalyticsPayload(state, courseId, sectionId)
      },
      state
    );
  };
}

export const analyticsLogClickStartQuizSession = withLogAndSwallowError(
  logClickStartQuizSession
);

function logClickStartQuizSession(courseId: string, sectionId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/CLICK_START_QUESTIONS_ONLY_SESSION`,
        payload: generateSessionAnalyticsPayload(state, courseId, sectionId)
      },
      state
    );
  };
}

export const analyticsLogQuitSession = withLogAndSwallowError(logQuitSession);

function logQuitSession(courseId: string, sectionId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/QUIT_SESSION`,
        payload: generateSessionAnalyticsPayload(state, courseId, sectionId)
      },
      state
    );
  };
}

export const analyticsLogSwitchSidebarTab =
  withLogAndSwallowError(logSwitchSidebarTab);

function logSwitchSidebarTab(target: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/SWITCH_SIDEBAR_TAB`,
        payload: { target }
      },
      getState()
    );
  };
}

export const analyticsLogClickSidebarRow =
  withLogAndSwallowError(logClickSidebarRow);

function logClickSidebarRow({
  courseId,
  sectionId,
  target
}: {
  courseId: string;
  sectionId: string;
  target: string;
}) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/CLICK_SIDEBAR_ROW`,
        payload: {
          ...generateSessionAnalyticsPayload(state, courseId, sectionId),
          target
        }
      },
      state
    );
  };
}

export const analyticsLogNumWrongAnswersDiff = withLogAndSwallowError(
  logNumWrongAnswersDiff
);

function logNumWrongAnswersDiff({
  courseId,
  sectionId,
  wrongAnswersOnStat,
  wrongAnswersOnCourse
}: {
  courseId: string;
  sectionId: string;
  wrongAnswersOnStat: number;
  wrongAnswersOnCourse: number;
}) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/NUM_WRONG_ANSWERS_DIFF`,
        payload: {
          ...generateSessionAnalyticsPayload(state, courseId, sectionId),
          wrongAnswersOnStat,
          wrongAnswersOnCourse
        }
      },
      state
    );
  };
}

export const analyticsLogWrongAnswersModeLocked = withLogAndSwallowError(
  logWrongAnswersModeLocked
);

function logWrongAnswersModeLocked({
  courseId,
  sectionId,
  dueToContentFormat,
  dueToDevice
}: {
  courseId: string;
  sectionId: string;
  dueToContentFormat: boolean;
  dueToDevice: boolean;
}) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    logGTMTrackingEvent(
      {
        category: analyticsCategories.SESSION,
        event: `${namespace}/WRONG_ANSWERS_MODE_LOCKED`,
        payload: {
          ...generateSessionAnalyticsPayload(state, courseId, sectionId),
          dueToContentFormat,
          dueToDevice
        }
      },
      state
    );
  };
}

export const analyticsLogAssignmentSessionStudied = withLogAndSwallowError(
  logAssignmentSessionStudied
);

function logAssignmentSessionStudied(sessionId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const sectionId = getSessionSectionId(state, sessionId);
    const sessionScore = getSessionScore(state, sessionId);
    const courseId = getSessionCourseId(state, sessionId);
    const assignments = makeGetAssignmentsForSection(sectionId)(state);

    assignments
      .filter(assignment => moment(assignment.dueDate).isAfter(new Date()))
      .forEach(assignment => {
        const { id, startDate, dueDate, classId } = assignment;
        const assignmentProgress = makeGetAssignmentProgress(
          id!,
          courseId!
        )(state);

        pushEnrichedEventToDataLayerWithCleanUp({
          event: `${namespace}/assignmentSessionComplete`,
          payload: {
            classId,
            sessionScore,
            assignmentProgress,
            assignmentStartDate: startDate,
            assignmentDueDate: dueDate,
            assignmentId: id
          }
        });
      });
  };
}

export const analyticsLogQuizAssignmentSessionStudied = withLogAndSwallowError(
  logQuizAssignmentSessionStudied
);

function logQuizAssignmentSessionStudied(quizId: string, sessionScore: number) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const assignments = makeGetAssignmentsForQuiz(quizId)(state);

    assignments
      .filter(assignment => moment(assignment.dueDate).isAfter(new Date()))
      .forEach(assignment => {
        const { id, startDate, dueDate, classId } = assignment;
        const assignmentProgress = getAssignmentProgress(state, id!);

        pushEnrichedEventToDataLayerWithCleanUp({
          event: `${namespace}/assignmentSessionComplete`,
          payload: {
            classId,
            sessionScore,
            assignmentProgress,
            assignmentStartDate: startDate,
            assignmentDueDate: dueDate,
            assignmentId: id
          }
        });
      });
  };
}

export const analyticsLogBreakoutEasterEggShown = withLogAndSwallowError(
  logBreakoutEasterEggShown
);

function logBreakoutEasterEggShown() {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${namespace}/breakoutEasterEggShown`,
    payload: {}
  });
}

export const analyticsLogBreakoutEasterEggClicked = withLogAndSwallowError(
  logBreakoutEasterEggClicked
);

function logBreakoutEasterEggClicked() {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${namespace}/breakoutEasterEggClicked`,
    payload: {}
  });
}

export function logCourseContinueLearning(payload: { sectionId: string }) {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${namespace}/courseContinueLearning`,
    payload
  });
}

export function logStartedSession() {
  pushEnrichedEventToDataLayerWithCleanUp({
    event: `${namespace}/start`,
    payload: {}
  });
}
